import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Dialog, DialogContent } from "./dialog";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "./command";
import {
  CalendarIcon,
  LayoutDashboardIcon,
  UsersIcon,
  SettingsIcon,
  FileTextIcon,
  CreditCardIcon,
  VideoIcon
} from "lucide-react";

export function GlobalCommandMenu() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const down = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runCommand = (command) => {
    setOpen(false);
    command();
  };

  return (
    <>
      <button 
        onClick={() => setOpen(true)}
        className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-500 bg-neutral-100 hover:bg-neutral-200 border border-transparent rounded-full transition-colors mr-2"
        title="Search (Cmd+K)"
      >
        <span className="text-xs font-semibold">Search</span>
        <kbd className="hidden sm:inline-flex items-center h-5 px-1.5 text-[10px] font-medium text-neutral-500 bg-white border border-neutral-300 rounded">
          <span className="text-xs mr-0.5">⌘</span>K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 overflow-hidden sm:max-w-[600px] border-none shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-2xl bg-transparent [&>button]:hidden">
          <Command className="rounded-2xl border-none ring-1 ring-slate-900/5 shadow-2xl">
            <CommandInput placeholder="Type a command or search..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup heading="Suggestions">
                <CommandItem onSelect={() => runCommand(() => setLocation("/"))} className="cursor-pointer group">
                  <LayoutDashboardIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Dashboard</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setLocation("/appointments"))} className="cursor-pointer group">
                  <CalendarIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Appointments</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setLocation("/patients"))} className="cursor-pointer group">
                  <UsersIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Patients</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setLocation("/video-call"))} className="cursor-pointer group">
                  <VideoIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Video Call</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading="Settings & Billing">
                <CommandItem onSelect={() => runCommand(() => setLocation("/reports"))} className="cursor-pointer group">
                  <FileTextIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Reports</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setLocation("/payment-billing"))} className="cursor-pointer group">
                  <CreditCardIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Billing</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
                <CommandItem onSelect={() => runCommand(() => setLocation("/settings/profile"))} className="cursor-pointer group">
                  <SettingsIcon className="mr-3 h-4 w-4 text-slate-400 group-hover:text-blue-600 group-aria-selected:text-blue-600" />
                  <span>Settings</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
